import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import container from 'src/container';
import avatarDefault from 'src/assets/images/client_default.png';
import { useAvatarSas } from './consumer';

const {
    cradle: { munduClient, storageService },
} = container;

type UseAvatarOptions = {
    firstName?: string;
    lastName?: string;
    displayName?: string;
    sasConsumerId?: string;
};

const buildInitials = (
    firstName?: string,
    lastName?: string,
    displayName?: string,
) => {
    const source =
        displayName?.trim() ||
        [firstName, lastName].filter(Boolean).join(' ').trim();

    if (!source) {
        return '';
    }

    const letters = source
        .split(/\s+/)
        .filter(Boolean)
        .map(part => part[0]?.toUpperCase())
        .filter(Boolean);

    return letters.slice(0, 2).join('');
};

export const useAvatar = (
    avatarId?: string,
    options: UseAvatarOptions = {},
) => {
    const [src, setSrc] = useState<string>(avatarDefault);
    const shouldFetch = Boolean(avatarId);
    const { firstName, lastName, displayName, sasConsumerId } = options;
    const { sas: organizationSas } = useAvatarSas(
        shouldFetch && !sasConsumerId,
    );
    const { data: entitySas } = useSWR(
        shouldFetch && sasConsumerId
            ? ['avatarSAS', 'entity', sasConsumerId]
            : null,
        () => munduClient.getEntityAvatarSas(sasConsumerId as string),
    );
    const sas = sasConsumerId ? entitySas : organizationSas;

    useEffect(() => {
        if (!avatarId || !sas) {
            setSrc(avatarDefault);
            return;
        }

        setSrc(storageService.getImageUrl(avatarId, sas) || avatarDefault);
    }, [avatarId, sas]);

    const initials = useMemo(
        () => buildInitials(firstName, lastName, displayName),
        [firstName, lastName, displayName],
    );

    const hasAvatar = Boolean(avatarId && sas);

    return {
        src: hasAvatar ? src : avatarDefault,
        isDefault: !hasAvatar,
        initials,
    };
};
